import type { Workbook } from "exceljs";
import type { TDocumentDefinitions } from "pdfmake/interfaces";
import { buildCompareViewModel, exportCompareCsv } from "./compare";
import { computeDatedFinance } from "./dated-finance";
import { computeDatedOwnership } from "./dated-ownership";
import { Model } from "./model";
import { resolveScenarioOwnershipInput } from "./scenario-ownership";
import {
  createManualFxSnapshot,
  formatReportingCurrency,
  type FxSnapshot,
} from "./reporting-currency";
import type { Scenario } from "./types";

export interface DealReportSettings {
  baselineId?: string;
  valuationDate?: string;
  endDate?: string;
  fx?: FxSnapshot;
  /** Snapshot callers retain their opaque comparison keys here. */
  columnIds?: string[];
}

export interface DatedReportScenario {
  id: string;
  name: string;
  method: "legacy-v1" | "dated-v2";
  sourceAssumptions: string[];
  pendingInputs: string[];
  /** Immutable, stripped input snapshots used to derive this report. */
  financeInput: Scenario["datedFinance"] | null;
  ownershipInput: ReturnType<typeof resolveScenarioOwnershipInput>;
  ledger: ReturnType<typeof computeDatedFinance>["ledger"];
  finance: ReturnType<typeof computeDatedFinance> | null;
  ownership: ReturnType<typeof computeDatedOwnership> | null;
}

type InputSnapshotRow = {
  scenario: string;
  section: "datedFinance" | "datedOwnership";
  field: string;
  value: string;
};

const unique = (items: readonly string[]) => [
  ...new Set(items.map((item) => item.trim()).filter(Boolean)),
];

const cloneSnapshot = <T>(value: T): T => structuredClone(value);

function displayInputValue(value: unknown): string {
  if (value === null) return "null";
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean")
    return String(value);
  return JSON.stringify(value);
}

function flattenInput(
  value: unknown,
  path: string,
  rows: InputSnapshotRow[],
  scenario: string,
  section: InputSnapshotRow["section"],
): void {
  if (value === undefined) return;
  if (value === null || typeof value !== "object") {
    rows.push({
      scenario,
      section,
      field: path,
      value: displayInputValue(value),
    });
    return;
  }
  if (Array.isArray(value)) {
    if (value.length === 0) {
      rows.push({ scenario, section, field: path, value: "[]" });
      return;
    }
    value.forEach((item, index) =>
      flattenInput(item, `${path}[${index}]`, rows, scenario, section),
    );
    return;
  }
  const entries = Object.entries(value as Record<string, unknown>);
  if (entries.length === 0) {
    rows.push({ scenario, section, field: path, value: "{}" });
    return;
  }
  for (const [key, item] of entries)
    flattenInput(item, path ? `${path}.${key}` : key, rows, scenario, section);
}

function sourceInputRows(report: DealReport): InputSnapshotRow[] {
  const rows: InputSnapshotRow[] = [];
  for (const scenario of report.scenarios) {
    if (scenario.financeInput)
      flattenInput(
        scenario.financeInput,
        "",
        rows,
        scenario.name,
        "datedFinance",
      );
    if (scenario.ownershipInput)
      flattenInput(
        scenario.ownershipInput,
        "",
        rows,
        scenario.name,
        "datedOwnership",
      );
  }
  return rows;
}

type ComparisonNote = {
  scenario: string;
  metric: string;
  note: string;
};

function comparisonNotes(report: DealReport): ComparisonNote[] {
  return uniqueComparisonNotes(
    report.comparison.rows.flatMap((row) =>
      row.cells.flatMap((cell) => {
        const scenario = report.scenarios.find(
          (item) => item.id === cell.scenarioId,
        );
        const notes = [
          cell.basis,
          cell.deltaSuppressed === "window"
            ? "window excludes investment cash flows"
            : undefined,
          cell.deltaSuppressed === "horizon"
            ? "different ownership horizon"
            : undefined,
          cell.deltaSuppressed === "method"
            ? "different calculation basis"
            : undefined,
          row.key.startsWith("datedReturns.") && cell.value === null
            ? !scenario?.finance?.complete
              ? "incomplete dated inputs suppress this return"
              : !scenario.finance.returnsSettled
                ? "unsettled debt at analysis end suppresses this return"
                : row.key.endsWith("Irr")
                  ? "cash-flow series does not have exactly one sign change; IRR is suppressed"
                  : row.key === "datedReturns.equityMultiple"
                    ? "cash-flow series does not contain both an equity investment and distribution; multiple is suppressed"
                    : undefined
            : undefined,
        ].filter((note): note is string => Boolean(note));
        return notes.map((note) => ({
          scenario: scenario?.name ?? cell.scenarioId,
          metric: row.label,
          note,
        }));
      }),
    ),
  );
}

function uniqueComparisonNotes(notes: ComparisonNote[]): ComparisonNote[] {
  const seen = new Set<string>();
  return notes.filter((note) => {
    const key = `${note.scenario}\u0000${note.metric}\u0000${note.note}`;
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });
}

export interface DealReport {
  generatedAt: string;
  settings: Required<Omit<DealReportSettings, "columnIds">> & {
    columnIds: string[];
  };
  scenarios: DatedReportScenario[];
  comparison: ReturnType<typeof buildCompareViewModel>;
}

const visibleComparisonRows = (report: DealReport) =>
  report.comparison.rows.filter(
    (row) =>
      row.cells.some((cell) => cell.value !== null) ||
      (row.key.startsWith("datedReturns.") &&
        report.scenarios.some((scenario) => scenario.method === "dated-v2")),
  );

const identityFx = () =>
  createManualFxSnapshot(
    "AUD",
    1,
    new Date().toISOString().slice(0, 10),
    "Identity rate",
  );

function dateDefaults(scenarios: Scenario[]): {
  valuationDate: string;
  endDate: string;
} {
  const dated = scenarios.find(
    (scenario) => scenario.calculationMethod === "dated-v2",
  )?.datedFinance;
  return {
    valuationDate: dated?.startDate ?? new Date().toISOString().slice(0, 10),
    endDate: dated?.endDate ?? new Date().toISOString().slice(0, 10),
  };
}

function sourceAssumptions(scenario: Scenario): string[] {
  if (scenario.calculationMethod !== "dated-v2")
    return ["Legacy-v1 source scenario"];
  return [
    `Source scenario: ${scenario.sourceScenarioId ?? "not recorded"}`,
    `Dated finance: ${scenario.datedFinance ? "present" : "missing"}`,
    `Dated ownership: ${scenario.datedOwnership ? "present" : "missing"}`,
  ];
}

/** A report is a re-derived immutable view. It never converts a legacy ModelResult
 * into a dated result, even when a dated scenario carries migration assumptions. */
export function buildDealReport(
  scenarios: Scenario[],
  settings: DealReportSettings = {},
): DealReport {
  const defaults = dateDefaults(scenarios);
  const columnIds =
    settings.columnIds ?? scenarios.map((scenario) => scenario.id);
  const fx = settings.fx ?? identityFx();
  const results = Object.fromEntries(
    scenarios.map((scenario, index) => [
      columnIds[index],
      Model.compute(scenario),
    ]),
  );
  const reportScenarios = scenarios.map(
    (scenario, index): DatedReportScenario => {
      if (scenario.calculationMethod !== "dated-v2" || !scenario.datedFinance) {
        return {
          id: columnIds[index],
          name: scenario.name,
          method: "legacy-v1",
          sourceAssumptions: sourceAssumptions(scenario),
          pendingInputs: [
            "Legacy calculation basis; dated ledger is not available.",
          ],
          financeInput: null,
          ownershipInput: null,
          ledger: [],
          finance: null,
          ownership: null,
        };
      }
      const finance = computeDatedFinance(scenario.datedFinance);
      const ownershipInput = resolveScenarioOwnershipInput(scenario, finance);
      const ownership = ownershipInput
        ? computeDatedOwnership(ownershipInput)
        : null;
      return {
        id: columnIds[index],
        name: scenario.name,
        method: "dated-v2",
        sourceAssumptions: sourceAssumptions(scenario),
        pendingInputs: unique([
          ...finance.unresolvedInputs,
          ...finance.issues.map((issue) => issue.message),
          ...finance.returnWarnings,
          ...(ownership?.issues ?? []),
          ...(ownership?.errors ?? []),
          ...(ownership?.suppressionReasons ?? []),
          ...(scenario.unresolvedInputNotes ?? []),
        ]),
        financeInput: cloneSnapshot(scenario.datedFinance),
        ownershipInput: ownershipInput ? cloneSnapshot(ownershipInput) : null,
        ledger: finance.ledger,
        finance,
        ownership,
      };
    },
  );
  const baselineId =
    settings.baselineId && columnIds.includes(settings.baselineId)
      ? settings.baselineId
      : (columnIds[0] ?? "");
  const valuationDate = settings.valuationDate ?? defaults.valuationDate;
  const endDate = settings.endDate ?? defaults.endDate;
  return {
    generatedAt: new Date().toISOString(),
    settings: { baselineId, valuationDate, endDate, fx, columnIds },
    scenarios: reportScenarios,
    comparison: buildCompareViewModel({
      scenarios,
      results,
      scenarioIds: columnIds,
      baselineId,
      valuationDate,
      endDate,
      fx,
    }),
  };
}

export function exportDealCsv(report: DealReport): string {
  const inputRows = sourceInputRows(report);
  const notes = comparisonNotes(report);
  const lines = [
    "DATED DEAL SNAPSHOT",
    `Generated,${report.generatedAt}`,
    `FX,1 AUD = ${report.settings.fx.rate} ${report.settings.fx.quote}`,
    `FX source,${report.settings.fx.source}`,
    `FX as of,${report.settings.fx.asOf}`,
    `Valuation date,${report.settings.valuationDate}`,
    `End date,${report.settings.endDate}`,
    ...report.scenarios.flatMap((scenario) =>
      scenario.sourceAssumptions.map((assumption) =>
        ["Source assumption", scenario.name, assumption].map(csv).join(","),
      ),
    ),
    "",
    "PENDING INPUTS",
    "Scenario,Calculation method,Pending input / issue",
    ...report.scenarios.flatMap((scenario) =>
      (scenario.pendingInputs.length
        ? scenario.pendingInputs
        : ["None recorded"]
      ).map((issue) =>
        [scenario.name, scenario.method, issue].map(csv).join(","),
      ),
    ),
    "",
    "DATED INPUT SNAPSHOT",
    "Scenario,Section,Field,Value",
    ...inputRows.map((row) =>
      [row.scenario, row.section, row.field, row.value].map(csv).join(","),
    ),
    "",
    "COMPARISON BASIS AND SUPPRESSIONS",
    "Scenario,Metric,Reason",
    ...notes.map((note) =>
      [note.scenario, note.metric, note.note].map(csv).join(","),
    ),
    "",
    exportCompareCsv({
      ...report.comparison,
      rows: visibleComparisonRows(report),
    }).trimEnd(),
    "",
    "DATED LEDGER (AUD native)",
    "Scenario,Date,Category,Description,AUD amount,Project,Operator,Facility",
  ];
  for (const scenario of report.scenarios)
    for (const line of scenario.ledger)
      lines.push(
        [
          scenario.name,
          line.date,
          line.category,
          line.description,
          line.amount,
          line.project,
          line.operator,
          line.facilityId ?? "",
        ]
          .map(csv)
          .join(","),
      );
  lines.push("", "OWNERSHIP LEDGER (AUD native)");
  lines.push(
    "Scenario,Date,Series,Description,AUD amount,Present value,Within horizon",
  );
  for (const scenario of report.scenarios)
    for (const line of scenario.ownership?.datedFlows ?? [])
      lines.push(
        [
          scenario.name,
          line.date,
          line.series,
          line.label,
          line.amount,
          line.pv,
          line.withinHorizon ? "yes" : "no",
        ]
          .map(csv)
          .join(","),
      );
  return `${lines.join("\n")}\n`;
}

const csv = (value: unknown) => {
  const text =
    typeof value === "string" && /^[=+\-@\t]/.test(value)
      ? `'${value}`
      : String(value ?? "");
  return /[",\n\r]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

export async function buildDealExcelWorkbook(
  report: DealReport,
): Promise<Workbook> {
  const exceljs = await import("exceljs");
  const ExcelWorkbook =
    (exceljs as unknown as { Workbook?: new () => Workbook }).Workbook ??
    (
      exceljs as unknown as {
        default?: { Workbook?: new () => Workbook };
      }
    ).default?.Workbook;
  if (!ExcelWorkbook)
    throw new Error("Excel workbook constructor is unavailable");
  const workbook = new ExcelWorkbook();
  workbook.creator = "TENURE";
  workbook.title = "Dated deal snapshot";
  workbook.subject = "Dated financial ledger and comparison snapshot";
  workbook.calcProperties.fullCalcOnLoad = true;
  const settings = workbook.addWorksheet("Snapshot settings");
  settings.addRows([
    ["DATED DEAL SNAPSHOT", ""],
    ["Reporting currency", report.settings.fx.quote],
    ["AUD to reporting FX", report.settings.fx.rate],
    ["FX source", report.settings.fx.source],
    ["FX as of", report.settings.fx.asOf],
    ["Valuation date", report.settings.valuationDate],
    ["End date", report.settings.endDate],
  ]);
  settings.getRow(1).font = { bold: true, color: { argb: "FFFFFF" } };
  settings.getRow(1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "17324D" },
  };
  settings.getColumn(1).width = 24;
  settings.getColumn(2).width = 24;
  const comparison = workbook.addWorksheet("Comparison");
  comparison.addRow(["DATED SNAPSHOT COMPARISON", "", ""]);
  comparison.addRow([
    "Metric",
    ...report.comparison.scenarioIds.flatMap((id) => [
      report.scenarios.find((scenario) => scenario.id === id)?.name ?? id,
      "Delta",
    ]),
  ]);
  for (const row of visibleComparisonRows(report)) {
    comparison.addRow([
      row.label,
      ...row.cells.flatMap((cell) => [
        cell.value ?? "",
        cell.deltaVsBaseline ?? "",
      ]),
    ]);
  }
  comparison.getRow(1).font = { bold: true, color: { argb: "FFFFFF" } };
  comparison.getRow(1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "17324D" },
  };
  comparison.getRow(2).font = { bold: true, color: { argb: "17324D" } };
  comparison.columns.forEach((column) => {
    column.width = 20;
  });
  const comparisonBasis = workbook.addWorksheet("Comparison basis");
  comparisonBasis.addRow(["Scenario", "Metric", "Basis / suppression"]);
  for (const note of comparisonNotes(report))
    comparisonBasis.addRow([note.scenario, note.metric, note.note]);
  if (comparisonBasis.rowCount === 1)
    comparisonBasis.addRow([
      "All scenarios",
      "—",
      "No special comparison caveats",
    ]);
  comparisonBasis.getRow(1).font = { bold: true, color: { argb: "FFFFFF" } };
  comparisonBasis.getRow(1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "A77A34" },
  };
  comparisonBasis.columns.forEach((column) => {
    column.width = 34;
  });
  const ledger = workbook.addWorksheet("Dated ledger AUD");
  ledger.addRow([
    "Scenario",
    "Date",
    "Category",
    "Description",
    "AUD amount",
    "Report amount",
    "Project",
    "Operator",
    "Facility",
  ]);
  for (const scenario of report.scenarios)
    for (const line of scenario.ledger) {
      const row = ledger.addRow([
        scenario.name,
        line.date,
        line.category,
        line.description,
        line.amount,
        null,
        line.project,
        line.operator,
        line.facilityId ?? "",
      ]);
      row.getCell(6).value = {
        formula: `E${row.number}*'Snapshot settings'!$B$3`,
        result: line.amount * report.settings.fx.rate,
      };
    }
  const lastLedgerRow = ledger.rowCount;
  const allLedger = report.scenarios.flatMap((scenario) => scenario.ledger);
  const nativeTotal = allLedger.reduce((sum, line) => sum + line.amount, 0);
  const projectCashTotal = allLedger.reduce(
    (sum, line) => sum + line.project,
    0,
  );
  const operatorCashTotal = allLedger.reduce(
    (sum, line) => sum + line.operator,
    0,
  );
  const sumFormula = (column: string) =>
    lastLedgerRow > 1 ? `SUM(${column}2:${column}${lastLedgerRow})` : "0";
  const signedTotal = ledger.addRow([
    "Signed ledger entries (not TDC)",
    "",
    "",
    "",
    { formula: sumFormula("E"), result: nativeTotal },
    { formula: sumFormula("F"), result: nativeTotal * report.settings.fx.rate },
  ]);
  const projectTotal = ledger.addRow([
    "Project cash total",
    "",
    "",
    "",
    "",
    "",
    { formula: sumFormula("G"), result: projectCashTotal },
  ]);
  const operatorTotal = ledger.addRow([
    "Operator cash total",
    "",
    "",
    "",
    "",
    "",
    "",
    { formula: sumFormula("H"), result: operatorCashTotal },
  ]);
  for (const total of [signedTotal, projectTotal, operatorTotal]) {
    total.font = { bold: true };
    total.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "EDE3D0" },
    };
  }
  ledger.getRow(1).font = { bold: true, color: { argb: "FFFFFF" } };
  ledger.getRow(1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "17324D" },
  };
  ledger.columns.forEach((column, index) => {
    column.width = index === 4 || index === 5 ? 16 : 20;
  });
  const ownershipLedger = workbook.addWorksheet("Ownership ledger AUD");
  ownershipLedger.addRow([
    "Scenario",
    "Date",
    "Series",
    "Description",
    "AUD amount",
    "Present value",
    "Within horizon",
  ]);
  for (const scenario of report.scenarios)
    for (const line of scenario.ownership?.datedFlows ?? [])
      ownershipLedger.addRow([
        scenario.name,
        line.date,
        line.series,
        line.label,
        line.amount,
        line.pv,
        line.withinHorizon ? "yes" : "no",
      ]);
  ownershipLedger.getRow(1).font = {
    bold: true,
    color: { argb: "FFFFFF" },
  };
  ownershipLedger.getRow(1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "17324D" },
  };
  ownershipLedger.columns.forEach((column, index) => {
    column.width = index === 3 ? 38 : 20;
  });
  const pending = workbook.addWorksheet("Pending inputs");
  pending.addRow(["Scenario", "Calculation method", "Pending input / issue"]);
  for (const scenario of report.scenarios)
    for (const issue of scenario.pendingInputs.length
      ? scenario.pendingInputs
      : ["None recorded"])
      pending.addRow([scenario.name, scenario.method, issue]);
  pending.getRow(1).font = { bold: true, color: { argb: "FFFFFF" } };
  pending.getRow(1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "A77A34" },
  };
  pending.columns.forEach((column) => {
    column.width = 32;
  });
  const sources = workbook.addWorksheet("Source assumptions");
  sources.addRow(["Scenario", "Calculation method", "Source assumption"]);
  for (const scenario of report.scenarios)
    for (const assumption of scenario.sourceAssumptions)
      sources.addRow([scenario.name, scenario.method, assumption]);
  sources.getRow(1).font = { bold: true, color: { argb: "FFFFFF" } };
  sources.getRow(1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "17324D" },
  };
  sources.columns.forEach((column) => {
    column.width = 34;
  });
  const inputs = workbook.addWorksheet("Dated input snapshot");
  inputs.addRow(["Scenario", "Section", "Field", "Value"]);
  for (const row of sourceInputRows(report))
    inputs.addRow([row.scenario, row.section, row.field, row.value]);
  inputs.getRow(1).font = { bold: true, color: { argb: "FFFFFF" } };
  inputs.getRow(1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "17324D" },
  };
  inputs.getColumn(1).width = 26;
  inputs.getColumn(2).width = 20;
  inputs.getColumn(3).width = 38;
  inputs.getColumn(4).width = 48;
  const programme = workbook.addWorksheet("Programme dates");
  programme.addRow(["Scenario", "Milestone", "Resolved date", "Status"]);
  for (const scenario of report.scenarios) {
    for (const [milestone, date] of Object.entries(
      scenario.finance?.resolvedMilestones ?? {},
    ))
      programme.addRow([scenario.name, milestone, date, "resolved"]);
    for (const issue of scenario.finance?.issues ?? [])
      if (issue.code.includes("milestone") || issue.code === "missing-data")
        programme.addRow([
          scenario.name,
          issue.itemId ?? "—",
          "—",
          issue.message,
        ]);
  }
  programme.getRow(1).font = { bold: true, color: { argb: "FFFFFF" } };
  programme.getRow(1).fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: "17324D" },
  };
  programme.columns.forEach((column) => {
    column.width = 30;
  });
  return workbook;
}

export function buildDealPdfDefinition(
  report: DealReport,
): TDocumentDefinitions {
  const inputRows = sourceInputRows(report);
  const notes = comparisonNotes(report);
  const currency = (value: number | null) =>
    value === null ? "—" : formatReportingCurrency(value, report.settings.fx);
  const comparisonValue = (key: string, columnId: string): number | null =>
    report.comparison.rows
      .find((row) => row.key === key)
      ?.cells.find((cell) => cell.scenarioId === columnId)?.value ?? null;
  const displayComparisonCell = (
    row: (typeof report.comparison.rows)[number],
    cell: (typeof row.cells)[number],
  ) => {
    if (cell.value !== null) return cell.display;
    const scenario = report.scenarios.find(
      (item) => item.id === cell.scenarioId,
    );
    const note = notes.find(
      (item) => item.scenario === scenario?.name && item.metric === row.label,
    )?.note;
    return note ? `— (${note})` : cell.display;
  };
  const content: TDocumentDefinitions["content"] = [
    { text: "DATED DEAL SNAPSHOT", style: "title" },
    {
      text: `Calculation basis: ${report.scenarios.map((scenario) => `${scenario.name} (${scenario.method})`).join(", ")}`,
    },
    {
      text: `Reporting: 1 AUD = ${report.settings.fx.rate} ${report.settings.fx.quote}; ${report.settings.fx.source}, ${report.settings.fx.asOf}`,
    },
    {
      text: `Date window: ${report.settings.valuationDate} to ${report.settings.endDate}`,
      margin: [0, 0, 0, 12],
    },
    { text: "Source assumptions", style: "heading" },
    {
      ul: report.scenarios.flatMap((scenario) =>
        scenario.sourceAssumptions.map(
          (assumption) => `${scenario.name}: ${assumption}`,
        ),
      ),
    },
    { text: "Comparison", style: "heading" },
    {
      table: {
        headerRows: 1,
        widths: ["*", ...report.comparison.scenarioIds.map(() => "auto")],
        body: [
          [
            "Metric",
            ...report.comparison.scenarioIds.map(
              (id) =>
                report.scenarios.find((scenario) => scenario.id === id)?.name ??
                id,
            ),
          ],
          ...visibleComparisonRows(report).map((row) => [
            row.label,
            ...row.cells.map((cell) => displayComparisonCell(row, cell)),
          ]),
        ],
      },
    },
    {
      text: "Common-window returns and full-source funding/ownership",
      style: "heading",
      pageBreak: "before",
    },
    {
      table: {
        headerRows: 1,
        widths: ["*", "auto", "auto", "auto"],
        body: [
          [
            "Scenario",
            "Project NPV (common window)",
            "Peak debt (full source horizon)",
            "Ownership advantage (full source horizon)",
          ],
          ...report.scenarios.map((scenario) => [
            scenario.name,
            currency(comparisonValue("datedReturns.projectNpv", scenario.id)),
            currency(scenario.finance?.peakDebt ?? null),
            currency(scenario.ownership?.advantage ?? null),
          ]),
        ],
      },
    },
    { text: "Pending inputs", style: "heading" },
    {
      ul: report.scenarios.flatMap((scenario) =>
        (scenario.pendingInputs.length
          ? scenario.pendingInputs
          : ["None recorded"]
        ).map((issue) => `${scenario.name}: ${issue}`),
      ),
    },
    { text: "Comparison basis and suppressions", style: "heading" },
    {
      table: {
        headerRows: 1,
        widths: ["auto", "*", "*"],
        body: [
          ["Scenario", "Metric", "Reason"],
          ...(notes.length
            ? notes.map((note) => [note.scenario, note.metric, note.note])
            : [["All scenarios", "—", "No special comparison caveats"]]),
        ],
      },
    },
    { text: "Programme dates", style: "heading" },
    {
      table: {
        headerRows: 1,
        widths: ["*", "*", "auto"],
        body: [
          ["Scenario", "Milestone", "Resolved date / status"],
          ...report.scenarios.flatMap((scenario) => {
            const resolved = Object.entries(
              scenario.finance?.resolvedMilestones ?? {},
            );
            const issues = (scenario.finance?.issues ?? []).filter(
              (issue) =>
                issue.code.includes("milestone") ||
                issue.code === "missing-data",
            );
            return [
              ...resolved.map(([id, date]) => [scenario.name, id, date]),
              ...issues.map((issue) => [
                scenario.name,
                issue.itemId ?? "—",
                issue.message,
              ]),
            ];
          }),
        ],
      },
    },
    {
      text: "Dated input snapshot",
      style: "heading",
      pageBreak: "before",
    },
    {
      table: {
        headerRows: 1,
        widths: ["auto", "auto", "*", "auto"],
        body: [
          ["Scenario", "Section", "Input field", "Value"],
          ...(inputRows.length
            ? inputRows.map((row) => [
                row.scenario,
                row.section,
                row.field || "(root)",
                row.value,
              ])
            : [["—", "—", "No dated inputs", "—"]]),
        ],
      },
    },
    {
      text: "Dated project and equity ledger (AUD native)",
      style: "heading",
      pageBreak: "before",
    },
    {
      table: {
        headerRows: 1,
        widths: ["auto", "auto", "*", "auto", "auto", "auto"],
        body: [
          ["Scenario", "Date", "Category", "Description", "Project", "Equity"],
          ...report.scenarios.flatMap((scenario) =>
            scenario.ledger.map((line) => [
              scenario.name,
              line.date,
              line.category,
              line.description,
              line.project.toLocaleString("en-AU", {
                maximumFractionDigits: 2,
              }),
              line.amount.toLocaleString("en-AU", { maximumFractionDigits: 2 }),
            ]),
          ),
        ],
      },
    },
    {
      text: "Ownership ledger (AUD native; source ownership horizon)",
      style: "heading",
      pageBreak: "before",
    },
    {
      table: {
        headerRows: 1,
        widths: [80, 55, 60, "*", 50, 50],
        body: [
          ["Scenario", "Date", "Series", "Description", "AUD", "PV"],
          ...report.scenarios.flatMap((scenario) =>
            (scenario.ownership?.datedFlows ?? []).map((line) => [
              scenario.name,
              line.date,
              line.series,
              line.label,
              line.amount.toLocaleString("en-AU", {
                maximumFractionDigits: 2,
              }),
              line.pv.toLocaleString("en-AU", { maximumFractionDigits: 2 }),
            ]),
          ),
        ],
      },
    },
  ];
  return {
    pageSize: "A4",
    pageMargins: [36, 42, 36, 42],
    defaultStyle: { font: "Roboto", fontSize: 8 },
    styles: {
      title: { fontSize: 20, bold: true, color: "17324D" },
      heading: {
        fontSize: 12,
        bold: true,
        color: "A77A34",
        margin: [0, 14, 0, 7],
      },
    },
    header: {
      text: "TENURE  /  DATED DEAL SNAPSHOT",
      color: "17324D",
      bold: true,
      fontSize: 8,
      margin: [36, 18, 36, 0],
    },
    footer: (page) => ({
      text: `TENURE  •  Dated deal snapshot  •  ${page}`,
      color: "6B7280",
      fontSize: 7,
      alignment: "right",
      margin: [0, 0, 36, 12],
    }),
    content,
  };
}

async function downloadBlob(blob: Blob, filename: string): Promise<void> {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  try {
    anchor.href = url;
    anchor.download = filename;
    document.body.appendChild(anchor);
    anchor.click();
  } finally {
    anchor.remove();
    URL.revokeObjectURL(url);
  }
}

export async function exportDealExcel(report: DealReport): Promise<void> {
  const workbook = await buildDealExcelWorkbook(report);
  await downloadBlob(
    new Blob([await workbook.xlsx.writeBuffer()], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    }),
    "dated-deal-snapshot.xlsx",
  );
}

export async function exportDealPdf(report: DealReport): Promise<void> {
  const [pdfmake, vfsModule] = await Promise.all([
    import("pdfmake/build/pdfmake"),
    import("pdfmake/build/vfs_fonts"),
  ]);
  const renderer =
    (
      pdfmake as {
        default?: {
          createPdf?: (definition: unknown) => { getBlob: () => Promise<Blob> };
        };
        createPdf?: (definition: unknown) => { getBlob: () => Promise<Blob> };
      }
    ).default ?? pdfmake;
  if (!renderer.createPdf) throw new Error("PDF renderer is unavailable");
  const vfs = (vfsModule as { default?: unknown }).default ?? vfsModule;
  if (
    "addVirtualFileSystem" in renderer &&
    typeof renderer.addVirtualFileSystem === "function"
  )
    renderer.addVirtualFileSystem(vfs as never);
  if ("addFonts" in renderer && typeof renderer.addFonts === "function")
    renderer.addFonts({
      Roboto: {
        normal: "Roboto-Regular.ttf",
        bold: "Roboto-Medium.ttf",
        italics: "Roboto-Italic.ttf",
        bolditalics: "Roboto-MediumItalic.ttf",
      },
    });
  await downloadBlob(
    await renderer.createPdf(buildDealPdfDefinition(report)).getBlob(),
    "dated-deal-snapshot.pdf",
  );
}
